/**
 * PhaseStepSkillEffect — Astral tree evolution: Blink with invulnerability landing.
 *
 * Phase Step replaces Blink (replacesId: "blink") at XP threshold 200.
 * Identical to Blink in mechanics, with one addition: the player gains
 * 0.25 seconds of invulnerability on landing (via AstralTeleportSystem).
 *
 * Resolver queries use scope 'blink' (the skill it replaces) so all
 * blink_reach and blink_cooldown upgrade wiring flows through automatically
 * without re-registration.
 *
 * Upgrade hooks (from blink chain, scope 'blink'):
 * - blink_reach: `rangeMultiplier` (multiplicative) — extends teleport range.
 * - blink_cooldown: `cooldownReduction` (additive, fraction) — reduces cooldown.
 *
 * Invulnerability extension (scope 'phase_step'):
 * - oracle_phase_step: `phaseStepInvulnerabilityMs` (additive) — bonus invuln ms
 *   during Oracle / Prescience, extending 250ms base to 500ms.
 *
 * The invuln override is refreshed on every activation so oracle_phase_step
 * changes take effect without requiring a Phase Step re-init.
 *
 * Pure logic — no Phaser dependencies.
 *
 * @see astral-spec.md § phase_step
 * @see design-decisions.md § Astral Base Values (base invuln 250ms)
 * @see ADR-064 (attunement shared systems architecture)
 */

import type { SkillEffect } from '../../SkillEffect.js';
import type { AstralTeleportSystem, TeleportEvent } from '../../../systems/AstralTeleportSystem.js';
import { TILE_SIZE } from '../../../utils/Constants.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface PhaseStepDeps {
  readonly resolver: {
    getMultiplicativeEffect(skillId: string, key: string): number;
    getAdditiveEffect(skillId: string, key: string): number;
  };
  readonly teleport: Pick<
    AstralTeleportSystem,
    'executeTeleport' | 'onTeleport' | 'setInvulnOverride' | 'update'
  >;
  readonly player: {
    getPosition(): { x: number; y: number };
  };
  readonly targeting: {
    getTargetPosition(): { x: number; y: number };
  };
  readonly xpSource?: {
    awardTeleportXp(dangerPresent: boolean): void;
    isDangerPresent(): boolean;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface PhaseStepConfig {
  readonly cooldownMs: number;
  readonly baseRangeTiles: number;
  readonly baseInvulnMs: number;
}

const DEFAULT_CONFIG: PhaseStepConfig = {
  cooldownMs: 400,       // fast cooldown tier (same as Blink)
  baseRangeTiles: 4,     // same base range as Blink
  baseInvulnMs: 250,     // 0.25s invuln on landing — the Phase Step difference
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class PhaseStepSkillEffect implements SkillEffect {
  readonly skillId = 'phase_step';
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;

  private readonly deps: PhaseStepDeps;
  private readonly config: PhaseStepConfig;

  private _cooldownRemaining = 0;
  private _teleportsFired = 0;
  private _resolvedCooldownMs = 0;
  private _resolvedRangeTiles = 0;
  private _resolvedInvulnMs = 0;
  private _lastTeleportFrom: { x: number; y: number } | null = null;
  private _lastTeleportTo: { x: number; y: number } | null = null;

  constructor(deps: PhaseStepDeps, config: Partial<PhaseStepConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };

    // Wire XP hook if xpSource provided.
    if (deps.xpSource) {
      const xp = deps.xpSource;
      deps.teleport.onTeleport((_event: TeleportEvent) => {
        xp.awardTeleportXp(xp.isDangerPresent());
      });
    }
  }

  // --- State getters ---

  get teleportsFired(): number { return this._teleportsFired; }
  get resolvedCooldownMs(): number { return this._resolvedCooldownMs; }
  get resolvedRangeTiles(): number { return this._resolvedRangeTiles; }
  get resolvedInvulnMs(): number { return this._resolvedInvulnMs; }
  get lastTeleportFrom(): Readonly<{ x: number; y: number }> | null { return this._lastTeleportFrom; }
  get lastTeleportTo(): Readonly<{ x: number; y: number }> | null { return this._lastTeleportTo; }
  get cooldown(): number { return this._cooldownRemaining; }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    return this._cooldownRemaining <= 0;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    // Resolve range (scope: 'blink' — inherits from replaced skill).
    const rangeMultiplier = this.deps.resolver.getMultiplicativeEffect('blink', 'rangeMultiplier');
    this._resolvedRangeTiles = this.config.baseRangeTiles * rangeMultiplier;
    const rangePx = this._resolvedRangeTiles * TILE_SIZE;

    // Resolve cooldown (scope: 'blink' — inherits from replaced skill).
    const cooldownReduction = this.deps.resolver.getAdditiveEffect('blink', 'cooldownReduction');
    this._resolvedCooldownMs = Math.max(100, this.config.cooldownMs * (1 - cooldownReduction));

    // Resolve invuln: base + oracle_phase_step additive bonus (scope: 'phase_step').
    const invulnBonus = this.deps.resolver.getAdditiveEffect('phase_step', 'phaseStepInvulnerabilityMs');
    this._resolvedInvulnMs = this.config.baseInvulnMs + invulnBonus;

    // Set invuln override BEFORE executeTeleport so the window starts on landing.
    this.deps.teleport.setInvulnOverride(this._resolvedInvulnMs);

    // Compute destination: clamp to max range.
    const from = this.deps.player.getPosition();
    const target = this.deps.targeting.getTargetPosition();

    const dx = target.x - from.x;
    const dy = target.y - from.y;
    const dist = Math.sqrt(dx * dx + dy * dy);

    let toX: number;
    let toY: number;

    if (dist <= 0.001) {
      toX = from.x;
      toY = from.y;
    } else if (dist <= rangePx) {
      toX = target.x;
      toY = target.y;
    } else {
      toX = from.x + (dx / dist) * rangePx;
      toY = from.y + (dy / dist) * rangePx;
    }

    this._lastTeleportFrom = { x: from.x, y: from.y };
    this._lastTeleportTo = { x: toX, y: toY };

    // Execute teleport — invuln window starts inside executeTeleport.
    this.deps.teleport.executeTeleport(from.x, from.y, toX, toY);
    this._teleportsFired++;

    // Start cooldown.
    this._cooldownRemaining = this._resolvedCooldownMs;

    return true;
  }

  deactivate(): void {
    // Instant — no toggle.
  }

  isActive(): boolean {
    return false;
  }

  update(delta: number): void {
    if (this._cooldownRemaining > 0) {
      this._cooldownRemaining = Math.max(0, this._cooldownRemaining - delta);
    }
    // Tick the teleport system's invuln window timer (in-game, the scene does
    // this; in the simulation harness Phase Step owns the tick).
    this.deps.teleport.update(delta);
  }

  destroy(): void {
    // Clear the invuln override when Phase Step is destroyed (e.g., skill removed).
    this.deps.teleport.setInvulnOverride(null);
    this._cooldownRemaining = 0;
  }
}
